import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TrailerService } from "../shared/trailer.service";
import { GetPictureService } from "../../shared/services/files/getPictures.service";
import { FileUploadService } from "../../shared/services/files/fileUpload.service";
import { ToastrService } from "ngx-toastr";
import { CanComponentDeactivate } from "../../shared/guards/confirmation/confirmation.guard";
import { MaskService } from "../../shared/services/mask/mask.service";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@models/permissions.enum";
import {
  TrailerViewModel,
  TrailerStatus,
} from "@app/shared/models/trailers/TrailerViewModel.model";
import { TractorStatus } from "@app/shared/models/tractors/TractorViewModel.model";
import { TerminalLocationsServices } from "../../shared/services/terminals/terminals.service";
import { StatesServices } from "../../shared/services/states/states.service";
import { of as observableOf } from "rxjs";

@Component({
  selector: "app-trailer-detail",
  templateUrl: "./trailer-detail.component.html",
  styleUrls: ["./trailer-detail.component.css"],
})
export class TrailerDetailComponent implements OnInit, CanComponentDeactivate {
  @ViewChild("profilePictureInput", { static: false }) profilePictureInput;

  trailer: TrailerViewModel = new TrailerViewModel();
  isNewTrailer: boolean = false;
  isEditMode: boolean = false;
  isProcessing: boolean = false;
  selectedTab: string = "profile";
  hasUnsavedChanges: boolean = false;

  // UI state
  profilePicture: any = "assets/img/trailer.jpg";
  previewProfilePicture: any;
  newProfileFile: any;
  originalInfo: any;

  // Data
  terminals: Array<string> = [];
  states: { abbreviation: string; fullName: string }[];

  // Masks
  dateInputMask: any;
  docType: string = "trailer";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private trailerService: TrailerService,
    private getPictureService: GetPictureService,
    private fileUploadService: FileUploadService,
    private toastr: ToastrService,
    private maskService: MaskService,
    private authService: authenticationService,
    private terminalLocationsServices: TerminalLocationsServices,
    private statesServices: StatesServices
  ) {}

  ngOnInit() {
    this.initializeMasks();
    this.terminals = this.terminalLocationsServices.getTerminalLocations();
    this.states = this.statesServices.getTwoLettersStates("US");

    this.route.queryParams.subscribe((queryParams) => {
      const tab = queryParams["tab"];
      if (tab) {
        this.selectedTab = tab;
      }
    });

    this.route.params.subscribe((params) => {
      const trailerId = params["trailerId"];

      if (trailerId === "newtrailer") {
        this.initializeNewTrailer();
      } else if (trailerId) {
        this.loadExistingTrailer(trailerId);
      }
    });
  }

  initializeMasks() {
    this.dateInputMask = this.maskService.getInputDateOnlyMask();
  }

  initializeNewTrailer() {
    this.isNewTrailer = true;
    this.isEditMode = true;
    this.trailer = new TrailerViewModel();
    this.trailer.isActive = true;
  }

  loadExistingTrailer(trailerId: string) {
    this.isNewTrailer = false;
    this.isEditMode = false;

    this.trailerService.gettrailerinfo(trailerId).subscribe((result: any) => {
      if (!result) {
        this.toastr.error("Trailer not found", "Error");
        return;
      }

      // Map the result to TrailerViewModel
      this.trailer = this.mapToTrailerViewModel(result);

      this.initDateFields(this.trailer);

      if (this.trailer.picture) {
        this.profilePicture = this.getFilePath(this.trailer.picture);
      }

      this.originalInfo = JSON.stringify(this.trailer);
    });
  }

  mapToTrailerViewModel(result: any): TrailerViewModel {
    const trailer = new TrailerViewModel();
    trailer.id = result.id || result.uuid;
    trailer.trailerNo = result.trailerNo || "";
    trailer.unitNumber = result.trailerNo || "";
    trailer.equipmentCode = result.equipmentCode || "";
    trailer.trailerType = result.trailerType || "";
    trailer.year = result.year
      ? typeof result.year === "string"
        ? parseInt(result.year)
        : result.year
      : null;
    trailer.make = result.make || "";
    trailer.model = result.model || "";
    trailer.mobileDeviceType = result.mobileDeviceType || "";
    trailer.mobileDeviceNumber = result.mobileDeviceNumber || "";
    trailer.serial = result.serial || "";
    trailer.vinNo = result.vinNo || "";
    trailer.terminalLocation = result.terminalLocation || "";
    trailer.currentLocationState = result.currentLocationState || "";
    trailer.currentLocationCity = result.currentLocationCity || "";
    trailer.purchasedDate =
      result.purchasedDate || result.PurchasedDate || result.datePurchased || null;
    trailer.purchasePrice = result.price
      ? typeof result.price === "string"
        ? parseFloat(result.price)
        : result.price
      : null;
    trailer.dateSold = result.dateSold || null;
    trailer.insuranceName = result.insuranceName || "";
    trailer.insurancePolicyNumber = result.insurancePolicyNumber || "";
    trailer.picture = result.picture || "";
    trailer.isActive =
      result.active !== undefined
        ? result.active
        : result.isActive !== undefined
          ? result.isActive
          : true;
    trailer.status = result.status !== undefined ? result.status : TractorStatus.Available;
    trailer.driverId = result.driverId || "";
    trailer.driver = result.driver || null;
    trailer.unNumbers = result.unNumbers || "";
    trailer.notes = result.notes || "";
    // Specs fields
    trailer.length = result.length || "";
    trailer.width = result.width || "";
    trailer.height = result.height || "";
    trailer.ladenWeight = result.ladenWeight || result.weight || "";
    trailer.unladenWeight = result.unladenWeight || "";
    trailer.axles = result.axles
      ? typeof result.axles === "string"
        ? result.axles
        : result.axles.toString()
      : "";
    trailer.tireSize = result.tireSize || "";
    trailer.engineHours = result.engineHours
      ? typeof result.engineHours === "string"
        ? result.engineHours
        : result.engineHours.toString()
      : "";
    // License fields
    trailer.stateOfRegistration = result.stateOfRegistration || "";
    trailer.licenseNumber = result.licenseNumber || "";
    trailer.licenseExpirationDate = result.licenseExpirationDate
      ? new Date(result.licenseExpirationDate)
      : null;
    // Maintenance fields
    trailer.nextAnnualService = result.nextAnnualService
      ? new Date(result.nextAnnualService)
      : null;
    trailer.nextFiveYearService = result.nextFiveYearService
      ? new Date(result.nextFiveYearService)
      : null;
    trailer.fleetillaLocationUpdatedUtc = result.fleetillaLocationUpdatedUtc
      ? new Date(result.fleetillaLocationUpdatedUtc)
      : null;
    return trailer;
  }

  hasPermission(permissionName: string): boolean {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  canViewProfileTab(): boolean {
    return this.hasPermission("TrailersRead") || this.hasPermission("TrailersChange");
  }

  canEditProfileTab(): boolean {
    return this.hasPermission("TrailersChange");
  }

  canViewSpecsTab(): boolean {
    return this.hasPermission("TrailersRead") || this.hasPermission("TrailersChange");
  }

  canEditSpecsTab(): boolean {
    return this.hasPermission("TrailersChange");
  }

  canViewLicenseTab(): boolean {
    return this.hasPermission("TrailersRead") || this.hasPermission("TrailersChange");
  }

  canEditLicenseTab(): boolean {
    return this.hasPermission("TrailersChange");
  }

  canViewMaintenanceTab(): boolean {
    return this.hasPermission("TrailersRead") || this.hasPermission("TrailersChange");
  }

  canEditMaintenanceTab(): boolean {
    return this.hasPermission("TrailersChange");
  }

  canViewTripDocumentsTab(): boolean {
    return this.hasPermission("TrailersRead") || this.hasPermission("TrailersChange");
  }

  canEditAnyTab(): boolean {
    return this.hasPermission("TrailersChange");
  }

  onTabClicked(tab: string) {
    this.selectedTab = tab;
  }

  onTabHasChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  toggleEditMode() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.originalInfo = JSON.stringify(this.trailer);
    } else {
      this.saveTrailer();
    }
  }

  cancelEdit() {
    if (this.isNewTrailer) {
      this.router.navigate(["/trailerlist"]);
    } else {
      this.isEditMode = false;
      this.resetChangesFlag();
      this.trailer = JSON.parse(this.originalInfo);
      this.initDateFields(this.trailer);

      if (this.trailer.picture) {
        this.profilePicture = this.getFilePath(this.trailer.picture);
      } else {
        this.profilePicture = "assets/img/trailer.jpg";
      }
      this.newProfileFile = null;
      this.previewProfilePicture = null;
    }
  }

  saveTrailer() {
    if (!this.validateTrailer(this.trailer)) {
      return;
    }

    this.isProcessing = true;

    if (this.isNewTrailer) {
      this.createNewTrailer();
    } else {
      this.updateExistingTrailer();
    }
  }

  createNewTrailer() {
    let profilePictureObservable;

    if (this.newProfileFile) {
      profilePictureObservable = this.uploadProfilePicture();
    } else {
      profilePictureObservable = observableOf(undefined);
    }

    profilePictureObservable.subscribe((result: any) => {
      if (result) {
        this.trailer.picture = result.name;
      }

      const trailerData = this.mapToTrailerData(this.trailer);
      this.trailerService.createNewTrailer(trailerData).subscribe({
        next: (trailerCreated: any) => {
          this.finishTrailerCreation(trailerCreated.id || trailerCreated.uuid);
        },
        error: (errors) => {
          this.handleErrors(errors);
          this.isProcessing = false;
        },
      });
    });
  }

  updateExistingTrailer() {
    if (this.newProfileFile) {
      this.uploadProfilePicture().subscribe((url: any) => {
        this.trailer.picture = url.name;
        this.saveTrailerChanges();
      });
    } else {
      this.saveTrailerChanges();
    }
  }

  saveTrailerChanges() {
    const trailerData = this.mapToTrailerData(this.trailer);
    this.trailerService.updateTrailer(trailerData).subscribe({
      next: () => {
        this.toastr.success("Trailer updated", "Success");
        this.isProcessing = false;
        this.isEditMode = false;
        this.resetChangesFlag();
        this.originalInfo = JSON.stringify(this.trailer);
      },
      error: (errors) => {
        this.handleErrors(errors);
        this.isProcessing = false;
      },
    });
  }

  mapToTrailerData(trailer: TrailerViewModel): any {
    return {
      id: trailer.id,
      trailerNo: trailer.trailerNo || trailer.unitNumber,
      equipmentCode: trailer.equipmentCode,
      trailerType: trailer.trailerType,
      year: trailer.year ? trailer.year.toString() : null,
      make: trailer.make,
      model: trailer.model,
      mobileDeviceType: trailer.mobileDeviceType,
      mobileDeviceNumber: trailer.mobileDeviceNumber,
      serial: trailer.serial,
      vinNo: trailer.vinNo,
      terminalLocation: trailer.terminalLocation,
      currentLocationState: trailer.currentLocationState,
      currentLocationCity: trailer.currentLocationCity,
      purchasedDate: trailer.purchasedDate,
      price: trailer.purchasePrice ? trailer.purchasePrice.toString() : null,
      insuranceName: trailer.insuranceName,
      insurancePolicyNumber: trailer.insurancePolicyNumber,
      picture: trailer.picture,
      active: trailer.isActive,
      status: trailer.status,
      driverId: trailer.driverId,
      unNumbers: trailer.unNumbers,
      notes: trailer.notes,
      length: trailer.length,
      width: trailer.width,
      height: trailer.height,
      ladenWeight: trailer.ladenWeight,
      unladenWeight: trailer.unladenWeight,
      axles: trailer.axles,
      tireSize: trailer.tireSize,
      engineHours: trailer.engineHours,
      stateOfRegistration: trailer.stateOfRegistration,
      licenseNumber: trailer.licenseNumber,
      licenseExpirationDate: trailer.licenseExpirationDate,
      nextAnnualService: trailer.nextAnnualService,
      nextFiveYearService: trailer.nextFiveYearService,
    };
  }

  finishTrailerCreation(trailerId: string) {
    this.toastr.success("Trailer created", "Success");
    this.isProcessing = false;
    this.isNewTrailer = false;
    this.isEditMode = false;
    this.resetChangesFlag();
    this.router.navigate([`/trailers/${trailerId}`]);
  }

  validateTrailer(trailer: TrailerViewModel): boolean {
    const trailerNo = trailer.trailerNo || trailer.unitNumber;
    if (!trailerNo || trailerNo.trim() === "") {
      this.toastr.warning("TRAILER NUMBER - Required", "Alert!");
      return false;
    }
    return true;
  }

  handleErrors(errors: any) {
    if (Array.isArray(errors)) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!", {
          closeButton: true,
          enableHtml: true,
        });
      }
    } else {
      this.toastr.error("An error occurred", "Error");
    }
  }

  // Image handling
  onProfilePictureSelected(event: any) {
    if (event.target.files && event.target.files[0]) {
      this.newProfileFile = event.target.files[0];
      this.previewProfilePicture = this.getPictureService.getPreviewImage(event.target.files[0]);
      this.profilePicture = this.previewProfilePicture;
    }
  }

  uploadProfilePicture() {
    const fname: string = this.newProfileFile.name;
    const prefix = this.trailer.trailerNo || this.trailer.unitNumber || "trailer";
    return this.fileUploadService.uploadSingleFileRename(
      [this.newProfileFile],
      this.docType,
      prefix + "_" + fname
    );
  }

  getFilePath(path: string) {
    return this.getPictureService.getPicturePath(path, this.docType);
  }

  // Date field initialization
  initDateFields(trailer: TrailerViewModel) {
    trailer.dateSold = this.convertToDate(trailer.dateSold);
    trailer.purchasedDate = this.convertToDate(trailer.purchasedDate);
    trailer.licenseExpirationDate = this.convertToDate(trailer.licenseExpirationDate);
    trailer.nextAnnualService = this.convertToDate(trailer.nextAnnualService);
    trailer.nextFiveYearService = this.convertToDate(trailer.nextFiveYearService);
  }

  convertToDate(date: any): Date | null {
    return date ? new Date(date) : null;
  }

  // Guard
  confirm(): boolean {
    if (this.hasUnsavedChanges || (this.isEditMode && !this.isNewTrailer)) {
      return window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    } else if (this.isNewTrailer) {
      return window.confirm("Leave this page without creating the trailer?");
    }
    return true;
  }

  resetChangesFlag() {
    this.hasUnsavedChanges = false;
  }

  getTrailerStatusLabel(): string {
    if (this.trailer?.status === undefined || this.trailer?.status === null) {
      return "Available";
    }
    const statusOptions = [
      { value: TractorStatus.Available, label: "Available" },
      { value: TractorStatus.Shop, label: "Shop" },
      { value: TractorStatus.OOS, label: "OOS" },
    ];
    const status = statusOptions.find((opt) => opt.value === this.trailer.status);
    return status ? status.label : "Available";
  }

  getStatusPillClass(): string {
    if (this.trailer?.status === undefined || this.trailer?.status === null) {
      return "status-pill status-in-service";
    }
    if (this.trailer.status === TractorStatus.Available) {
      return "status-pill status-in-service";
    } else if (this.trailer.status === TractorStatus.Shop) {
      return "status-pill status-out-of-service";
    } else if (this.trailer.status === TractorStatus.OOS) {
      return "status-pill status-out-of-service";
    }
    return "status-pill status-in-service";
  }

  getAssignedDriverName(): string {
    if (this.trailer?.driver) {
      const name = this.trailer.driver.name || this.trailer.driver.Name || "";
      const lastName = this.trailer.driver.lastName || this.trailer.driver.LastName || "";
      const fullName = `${name} ${lastName}`.trim();
      return fullName || "N/A";
    }
    return "N/A";
  }

  getLocationInfo(): string {
    return this.trailer?.currentLocationCity || this.trailer?.currentLocationState
      ? [this.trailer.currentLocationCity, this.trailer.currentLocationState]
          .filter(Boolean)
          .join(", ")
      : "-";
  }

  getFleetillaLastUpdated(): string {
    const raw = this.trailer?.fleetillaLocationUpdatedUtc;
    if (!raw) return "";
    const d = typeof raw === "string" ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return "";

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return "Updated <1h ago";
    } else if (diffHours < 24) {
      return `Updated ${diffHours}h ago`;
    } else if (diffDays === 1) {
      return "Updated 1d ago";
    } else {
      return `Updated ${diffDays}d ago`;
    }
  }

  getFleetillaLastUpdatedTooltip(): string {
    const raw = this.trailer?.fleetillaLocationUpdatedUtc;
    if (!raw) return "";
    const d = typeof raw === "string" ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString();
  }

  isFleetillaLocationStale(): boolean {
    const raw = this.trailer?.fleetillaLocationUpdatedUtc;
    if (!raw) return false;
    const d = typeof raw === "string" ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return false;

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours > 48;
  }

  deleteTrailer() {
    if (confirm("Are you sure you want to delete this trailer?")) {
      this.isProcessing = true;
      const trailerId = this.trailer.id || (this.trailer as any).uuid;
      this.trailerService.delete({ uuid: trailerId }).subscribe({
        next: () => {
          this.toastr.success("Trailer deleted", "Success");
          this.isProcessing = false;
          this.router.navigate(["/trailerlist"]);
        },
        error: (errors) => {
          this.handleErrors(errors);
          this.isProcessing = false;
        },
      });
    }
  }
}
